import React, { useState } from "react";

export interface Column<T> {
  header: string | ((props: { column: Column<T> }) => React.ReactNode);
  key?: keyof T;
  accessorKey?: string;
  cell?: (row: { original: T }) => React.ReactNode;
  toggleSorting: (desc?: boolean) => void;
  getIsSorted: () => "asc" | "desc" | undefined;
}

interface TableProps<T> {
  columns: Column<T>[];
  data: T[];
  onRowClick?: (row: T) => void;
  maxHeight?: string;
}

const ScrollTable = <T extends Record<string, any>>({
  columns,
  data,
  onRowClick,
  maxHeight = "500px",
}: TableProps<T>) => {
  const [sorting, setSorting] = useState<{
    key: keyof T;
    direction: "asc" | "desc" | undefined;
  } | null>(null);

  const toggleColumnSorting = (key: keyof T, isAsc: boolean) => {
    setSorting((prev) => {
      if (!prev || prev.key !== key) {
        return { key, direction: "asc" };
      }

      return {
        key,
        direction: isAsc ? "desc" : "asc",
      };
    });
  };

  const getSortedData = () => {
    if (!sorting) return data;

    const { key, direction } = sorting;

    if (typeof key === "string") {
      const [mainKey, subKey] = key.split(".");

      return [...data].sort((a, b) => {
        const aValue = subKey ? a[mainKey][subKey] : a[mainKey];
        const bValue = subKey ? b[mainKey][subKey] : b[mainKey];

        const aValueTrimmed =
          typeof aValue === "string" ? aValue.trim().toLowerCase() : aValue;
        const bValueTrimmed =
          typeof bValue === "string" ? bValue.trim().toLowerCase() : bValue;

        const aDate = new Date(aValueTrimmed);
        const bDate = new Date(bValueTrimmed);

        if (!isNaN(aDate.getTime()) && !isNaN(bDate.getTime())) {
          return direction === "asc"
            ? aDate.getTime() - bDate.getTime()
            : bDate.getTime() - aDate.getTime();
        }

        if (
          typeof aValueTrimmed === "number" &&
          typeof bValueTrimmed === "number"
        ) {
          return direction === "asc"
            ? aValueTrimmed - bValueTrimmed
            : bValueTrimmed - aValueTrimmed;
        } else if (
          typeof aValueTrimmed === "string" &&
          typeof bValueTrimmed === "string"
        ) {
          return direction === "asc"
            ? aValueTrimmed > bValueTrimmed
              ? 1
              : aValueTrimmed < bValueTrimmed
              ? -1
              : 0
            : bValueTrimmed > aValueTrimmed
            ? 1
            : bValueTrimmed < aValueTrimmed
            ? -1
            : 0;
        } else if (typeof aValueTrimmed === "number") {
          return -1; 
        } else {
          return 1; 
        }
      });
    }

    return data;
  };

  const sortedData = getSortedData();

  const columnsWithSorting = columns.map((column) => ({
    ...column,
    toggleSorting: (isAsc?: boolean) =>
      toggleColumnSorting(column.accessorKey as keyof T, isAsc ?? false),
    getIsSorted: () =>
      sorting && sorting.key === column.accessorKey
        ? sorting.direction
        : undefined,
  }));

  return (
    <div className="rounded-md border border-gray-300 overflow-hidden">
      <div className="overflow-y-auto h-[calc(100vh-250px)]" >
        <table className="min-w-full border-collapse bg-white">
          <thead className="bg-gray-100 sticky top-0 z-10">
            <tr>
              {columnsWithSorting.map((column, index) => (
                <th
                  key={index}
                  className="py-2 text-left text-gray-600 border-b border-gray-300 cursor-pointer"
                >
                  {typeof column.header === "function"
                    ? column.header({ column })
                    : column.header}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {sortedData.length > 0 ? (
              sortedData.map((originalRow, rowIndex) => (
                <tr
                  key={rowIndex}
                  className="hover:bg-gray-50 cursor-pointer"
                  onClick={() => onRowClick && onRowClick(originalRow)}
                >
                  {columnsWithSorting.map((column, colIndex) => (
                    <td
                      key={colIndex}
                      className={`py-2 px-4 text-gray-800 ${
                        rowIndex === sortedData.length - 1
                          ? ""
                          : "border-b border-gray-300"
                      }`}
                    >
                      {(() => {
                        if (column.cell && originalRow) {
                          return column.cell({ original: originalRow });
                        }
                        if (!column.cell && column.accessorKey && originalRow) {
                          const [mainKey, subKey] =
                            column.accessorKey.split(".");
                          return (
                            <span>{originalRow[mainKey][subKey] || ""}</span>
                          );
                        }
                      })()}
                    </td>
                  ))}
                </tr>
              ))
            ) : (
              <tr>
                <td colSpan={columns.length} className="py-2 px-4 text-center">
                  No data
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
};

export default ScrollTable;
